import React, { FC, useMemo, useState } from 'react';
import { IconButton, Paper, Table, TableBody, TableContainer, TableRow } from '@mui/material';
import TableErrorComponent from '../General/TableErrorComponent';
import { Spinner } from '../General/Spinner';
import { ISubscription, ISubscriptionRowData } from '../../types/subscription';
import subscriptionHeaderCells from './SubscriptionsHeaderCells';
import { subscriptionRowCellConfig } from './SubscriptionsRowCellConfig';
import { ArrowForward } from '@mui/icons-material';
import StyledTableCell from '../General/StyledTableCell';
import { CustomRow } from '../General/Table/CustomRow';
import TableHeadWithSorting from '../General/Table/TableHeadWithSorting';
import { Sorting } from '../../util/sortingUtils';
import CustomTablePagination from '../General/Table/CustomTablePagination';
import { useNavigate } from 'react-router-dom';

interface SubscriptionsTableContainerProps {
  subscriptions: ISubscription[];
  loading: boolean;
  error: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
}

function mapSubscriptionsToRowData(subscriptions: ISubscription[]): ISubscriptionRowData[] {
  const rows: ISubscriptionRowData[] = [];
  subscriptions?.forEach((subscription) => {
    subscription.roleAssignments?.forEach((roleAssignment) => {
      rows.push({
        subscriptionId: subscription.id,
        system: subscription.system,
        roleName: roleAssignment.roleName,
      });
    });
  });
  return rows;
}

const SubscriptionsTableContainer: FC<SubscriptionsTableContainerProps> = ({
  subscriptions,
  loading,
  error,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}) => {
  const [sorting, setSorting] = useState<Sorting<ISubscriptionRowData>>({
    orderBy: undefined,
    order: 'asc',
  });

  const subscriptionRows = useMemo(
    () => mapSubscriptionsToRowData(subscriptions ?? []),
    [subscriptions]
  );

  const navigate = useNavigate();

  const handleNavigateToSubscription = (_subscriptionId: string, _subscriptionSystem: string) => {
    navigate(`/subscriptions/${_subscriptionSystem}/${_subscriptionId}`);
  };

  return (
    <TableContainer component={Paper}>
      {!error && !loading && (
        <>
          <Table style={{ borderSpacing: '0' }}>
            <TableHeadWithSorting<ISubscriptionRowData>
              headCells={subscriptionHeaderCells}
              sorting={sorting}
              onSortingChange={setSorting}
            />
            <TableBody>
              {subscriptionRows.map((row, index) => (
                <TableRow
                  key={index}
                  onClick={() => handleNavigateToSubscription(row.subscriptionId, row.system)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}>
                  <CustomRow row={row} rowCellConfig={subscriptionRowCellConfig} />
                  <StyledTableCell label={'actions'} style={{ textAlign: 'right' }}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigateToSubscription(row.subscriptionId, row.system);
                      }}
                      title="View subscription details">
                      <ArrowForward />
                    </IconButton>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <CustomTablePagination
            collection={subscriptionRows}
            page={page}
            rowsPerPage={pageSize}
            totalCount={totalCount}
            setNewPage={onPageChange}
            setNewRowsPerPage={onPageSizeChange}
          />
        </>
      )}
      {error && <TableErrorComponent message={'An error occurred, data could not be loaded.'} />}
      {!error && loading && <Spinner />}
    </TableContainer>
  );
};

export default SubscriptionsTableContainer;
